<template>
  <div class="stock-search">
    <!-- Selected Stocks Display with Integrated Search -->
    <div class="selected-stocks">
      <div class="space-y-2">
        <!-- Existing Selected Stocks -->
        <div
            v-for="stock in selectedStocks"
            :key="stock.symbol"
            class="flex items-center justify-between p-3 bg-base-200 rounded-lg"
        >
          <div class="flex items-center space-x-3 flex-1">
            <div class="flex flex-col">
              <span class="font-bold">{{ stock.symbol }}</span>
              <span class="text-sm opacity-70">{{ stock.name }}</span>
            </div>
            <span class="badge badge-outline">{{ stock.sector }}</span>
          </div>

          <!-- Weight Input -->
          <div v-if="allowWeights" class="flex items-center space-x-2 mr-2">
            <input
                v-model="stockWeights[stock.symbol]"
                type="number"
                step="1"
                min="0"
                max="100"
                placeholder="Weight %"
                class="input input-bordered input-sm w-20"
                @input="updateWeight(stock.symbol, $event.target.value)"
            />
            <span class="text-sm text-base-content/70">%</span>
          </div>

          <button
              @click="removeStock(stock)"
              class="btn btn-sm btn-ghost text-error"
              :disabled="disabled"
          >
            <i class="mdi mdi-close h-4 w-4"></i>
          </button>
        </div>

        <!-- Search Input (only shown if max stocks not reached) -->
        <div v-if="!maxStocks || selectedStocks.length < maxStocks" class="relative">
          <input
              v-model="searchQuery"
              type="text"
              :placeholder="placeholder || 'Enter stock symbol or company name...'"
              class="input input-bordered w-full pr-10"
              @input="handleSearch"
              @focus="showResults = true"
              @blur="handleBlur"
              :disabled="disabled"
          />
          <div v-if="loading" class="absolute right-3 top-3">
            <span class="loading loading-spinner loading-sm"></span>
          </div>
        </div>

        <!-- Search Results -->
        <div v-if="showResults && searchResults.length > 0" class="search-results">
          <div class="card bg-base-100 shadow-lg border border-base-300">
            <div class="card-body p-0">
              <ul class="menu">
                <li v-for="stock in searchResults" :key="stock.symbol">
                  <button
                      @click="selectStock(stock)"
                      class="flex justify-between items-center p-3 hover:bg-base-200"
                  >
                    <div class="flex flex-col items-start">
                      <span class="font-bold text-sm">{{ stock.symbol }}</span>
                      <span class="text-xs opacity-70">{{ stock.name }}</span>
                    </div>
                    <div class="text-right">
                      <span class="badge badge-outline badge-sm">{{ stock.sector }}</span>
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- No Results Message -->
        <div v-if="showResults && searchQuery && searchResults.length === 0 && !loading" class="mt-2">
          <div class="alert alert-info">
            <i class="mdi mdi-information-outline w-6 h-6"></i>
            <span>No stocks found matching "{{ searchQuery }}"</span>
          </div>
        </div>
      </div>

      <!-- Validation Messages -->
      <div class="mt-3 space-y-1">
        <!-- Stock Count Validation -->
        <div v-if="maxStocks" class="flex items-center gap-2 text-sm">
          <i v-if="selectedStocks.length >= maxStocks" class="mdi mdi-check-circle text-success"></i>
          <i v-else class="mdi mdi-circle-outline text-base-content/50"></i>
          <span class="text-base-content/70">
            {{ selectedStocks.length }}/{{ maxStocks }} stocks selected
          </span>
        </div>

        <!-- Weight Validation -->
        <div v-if="allowWeights" class="flex items-center gap-2 text-sm">
          <i v-if="totalWeight === 100" class="mdi mdi-check-circle text-success"></i>
          <i v-else class="mdi mdi-circle-outline text-base-content/50"></i>
          <span class="text-base-content/70">Total Weight:</span>
          <span :class="totalWeight === 100 ? 'text-success font-medium' : 'text-error font-medium'">
            {{ totalWeight }}%
          </span>
          <span v-if="totalWeight !== 100" class="text-error text-xs">
            (must equal 100%)
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, watch, nextTick, computed} from 'vue'
import {useCompetitionStore} from '../stores/competition'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Enter stock symbol or company name...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  maxStocks: {
    type: Number,
    default: null
  },
  allowWeights: {
    type: Boolean,
    default: false
  },
  weights: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'stock-selected', 'stock-removed', 'update:weights'])

const competitionStore = useCompetitionStore()

const searchQuery = ref('')
const searchResults = ref([])
const loading = ref(false)
const showResults = ref(false)

// Use computed property for selectedStocks to avoid recursion
const selectedStocks = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Use computed property for stockWeights to sync with parent
const stockWeights = computed({
  get: () => props.weights,
  set: (value) => emit('update:weights', value)
})

const totalWeight = computed(() => {
  if (!props.allowWeights) return 0
  return Object.values(stockWeights.value).reduce((sum, weight) => {
    const numWeight = parseInt(weight) || 0
    return sum + numWeight
  }, 0)
})

const handleSearch = async () => {
  if (!searchQuery.value || searchQuery.value.length < 1) {
    searchResults.value = []
    return
  }

  loading.value = true
  try {
    const results = await competitionStore.searchStocks(searchQuery.value, 10)
    searchResults.value = results
  } catch (error) {
    console.error('Error searching stocks:', error)
    searchResults.value = []
  } finally {
    loading.value = false
  }
}

const selectStock = (stock) => {
  // Check if stock is already selected
  const isAlreadySelected = selectedStocks.value.some(s => s.symbol === stock.symbol)
  if (isAlreadySelected) {
    return
  }

  // Check if we've reached the maximum number of stocks
  if (props.maxStocks && selectedStocks.value.length >= props.maxStocks) {
    return
  }

  // Create new array with the new stock
  const newSelectedStocks = [...selectedStocks.value, stock]
  selectedStocks.value = newSelectedStocks
  emit('stock-selected', stock)

  // Clear search and hide results
  searchQuery.value = ''
  searchResults.value = []
  showResults.value = false
}

const removeStock = (stock) => {
  const index = selectedStocks.value.findIndex(s => s.symbol === stock.symbol)
  if (index !== -1) {
    // Create new array without the removed stock
    const newSelectedStocks = selectedStocks.value.filter(s => s.symbol !== stock.symbol)
    selectedStocks.value = newSelectedStocks
    emit('stock-removed', stock)
  }
}

const handleBlur = () => {
  // Delay hiding results to allow for clicks
  setTimeout(() => {
    showResults.value = false
  }, 200)
}

const updateWeight = (symbol, value) => {
  const newWeights = {...stockWeights.value}
  newWeights[symbol] = value
  stockWeights.value = newWeights
}

// Debounced search
let searchTimeout = null
watch(searchQuery, (newQuery) => {
  clearTimeout(searchTimeout)
  if (newQuery && newQuery.length >= 1) {
    searchTimeout = setTimeout(() => {
      handleSearch()
    }, 300)
  } else {
    searchResults.value = []
  }
})
</script>

<style scoped>
.stock-search {
  position: relative;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  max-height: 300px;
  overflow-y: auto;
}

.search-results .card {
  border-radius: 0.5rem;
}

.search-results .menu {
  max-height: 300px;
  overflow-y: auto;
}

.search-results .menu li {
  border-bottom: 1px solid hsl(var(--b3));
}

.search-results .menu li:last-child {
  border-bottom: none;
}
</style> 